import React, { useState, useEffect } from 'react'
import debounce from 'lodash/debounce'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import PlaylistActions, { thunks as PlayListThunks } from 'reducers/PlayList'
import ModalActions from 'reducers/Modal'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { useDispatch, connect } from 'react-redux'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import {
  Button,
  TextField,
  MaterialIcon,
  Modal
} from '@dsny/dsny-component-library'
import {
  Title,
  XButtonWrapper,
  ButtonWrapper,
  DescriptionCounter
} from './CreatePlaylistModal.styles'
import { StyledModal } from '../Modals.styles'

const MAX_TITLE_LIMIT = 50
const MAX_DESCRIPTION_LIMIT = 100

const mapStateToProps = ({ playList, modal }) => ({
  playlist: playList?.playlistUnderEditing,
  songsToAdd: playList?.songsToAdd,
  showPlaylistModal: modal?.showPlaylistModal
})

function CreatePlaylistModal({ playlist, songsToAdd = [], showPlaylistModal }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const [title, setTitle] = useState(playlist?.name || '')
  const [message, setMessage] = useState(playlist?.desc || '')
  const [hasTitleError, setHasTitleError] = useState(false)

  const checkTitle = debounce(() => {
    if (title) {
      setHasTitleError(false)
    }
  }, 300)

  useEffect(() => {
    return () => {
      dispatch(PlaylistActions.setPlaylistUnderEditing())
    }
  }, [])

  useEffect(() => {
    checkTitle()
  }, [title])

  const handleCreateButtonClick = () => {
    if (!title) {
      setHasTitleError(true)

      return
    }
    if (!playlist) {
      dispatch(
        PlayListThunks.create(
          { name: title, desc: message, default: false },
          ({ playlistid }) => {
            if (Array.isArray(songsToAdd) && songsToAdd.length > 0) {
              dispatch(PlayListThunks.addTracks(playlistid, songsToAdd))
              dispatch(PlaylistActions.setSongsToAdd([]))
            }
            dispatch(
              NotificationActions.broadcastMessage(
                title,
                NotificationType.playlistCreated
              )
            )
            handleCancelButtonClick()
          }
        )
      )
    } else {
      const newPlaylist = { ...playlist, name: title, desc: message }
      dispatch(
        PlayListThunks.updatePlaylist(newPlaylist, () => {
          dispatch(
            NotificationActions.broadcastMessage(
              title,
              NotificationType.playlistUpdated
            )
          )
          handleCancelButtonClick()
        })
      )
    }
  }

  const handleCancelButtonClick = () => {
    dispatch(ModalActions.togglePlaylistModal())
  }

  const updateText = (value, field, limit) => {
    if (value.length <= limit) {
      const setters = {
        title: setTitle,
        message: setMessage
      }

      if (setters[field]) {
        setters[field](value)
      }
    }
  }

  return (
    <StyledModal
      $isMobile={isMobile}
      isOpen={showPlaylistModal}
      onClose={handleCancelButtonClick}
      containerStyles={{
        padding: isMobile ? '48px 16px' : '24px 64px 64px'
      }}
    >
      <XButtonWrapper onClick={handleCancelButtonClick}>
        <MaterialIcon name="X" color={theme.colors.neutralW80} />
      </XButtonWrapper>

      <Title>{t('playlist.create-a-new-playlist')}</Title>

      <TextField
        autoFocus
        wrapperStyles={{ marginBottom: 24, paddingRight: 0 }}
        value={title}
        required
        error={hasTitleError ? t('playlist.playlist-name-input-error') : ''}
        errorStyle={{ marginTop: 8, color: theme.colors.error70 }}
        labelText={t('playlist.playlist-name')}
        setting="player"
        onChange={(event) => {
          updateText(event.currentTarget.value, 'title', MAX_TITLE_LIMIT)
        }}
      />

      <TextField
        multiline
        wrapperStyles={{ paddingRight: 0, marginBottom: 4 }}
        maxLength={MAX_DESCRIPTION_LIMIT}
        value={message}
        onChange={(event) => {
          updateText(
            event.currentTarget.value,
            'message',
            MAX_DESCRIPTION_LIMIT
          )
        }}
        labelText={t('playlist.description-optional-label')}
        setting="player"
      />

      <DescriptionCounter>{`${message?.length}/${MAX_DESCRIPTION_LIMIT}`}</DescriptionCounter>

      <ButtonWrapper>
        <Button
          variant="primary"
          setting="player"
          mode="dark"
          onClick={handleCreateButtonClick}
        >
          {playlist
            ? t('playlist.update-playlist')
            : t('playlist.create-playlist')}
        </Button>
      </ButtonWrapper>
    </StyledModal>
  )
}

export default connect(mapStateToProps)(CreatePlaylistModal)
